import { useEffect, useState } from "react";
import type { OwnerMissionView } from "../api";
import { api } from "../api";
import { JarvisOrb } from "../orb";
import { StateMessage, StatusBadge, Timeline } from "../design-system";
import { deriveAgentOsOrbState, recordLabel, recordStatus, type AgentOsStudioRecord } from "./agentOsStudioModel";
import { OwnerMissionControl, ownerMissionOrbState } from "./OwnerMissionControl";

export function OrbMissionConstellation({
  runs,
  timeline,
  mission,
  enabled = false,
}: {
  runs: AgentOsStudioRecord[];
  timeline?: AgentOsStudioRecord[];
  mission?: OwnerMissionView | null;
  enabled?: boolean;
}): JSX.Element {
  const registryState = deriveAgentOsOrbState(runs);
  const [currentMission, setCurrentMission] = useState<OwnerMissionView | null>(mission ?? null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => setCurrentMission(mission ?? null), [mission]);

  async function act(action: "pause" | "resume" | "stop"): Promise<void> {
    if (!currentMission || !enabled) return;
    setBusy(true);
    setError(null);
    try {
      const result = await api.agentOrbMissionAction(currentMission.runId, action);
      setCurrentMission(result.mission);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Owner-Mission-Aktion fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3" data-orb-mission-constellation>
      <div className="flex items-center gap-3 rounded-lg border border-white/10 bg-black/15 p-3">
        <JarvisOrb state={currentMission ? ownerMissionOrbState(currentMission) : registryState === "executing" ? "acting" : registryState === "waiting_for_owner" ? "confirming" : registryState === "failed" ? "error" : "idle"} size={74} assistantName="SIMO" />
        <div>
          <p className="text-[10px] uppercase tracking-[0.14em] text-jarvis-accent">Mission Constellation</p>
          <p className="mt-1 text-sm font-semibold text-jarvis-text">{currentMission?.phase ?? registryState}</p>
          <p className="mt-1 text-[10px] text-jarvis-muted">{runs.length} kanonische Runs · {timeline?.length ?? 0} Timeline-Events</p>
        </div>
      </div>
      {runs.length > 0 ? (
        <ul className="space-y-1.5">
          {runs.slice(0, 8).map((run, index) => (
            <li key={`${recordLabel(run)}-${index}`} className="flex justify-between gap-2 rounded border border-white/10 px-2.5 py-2 text-[11px]">
              <span className="truncate text-jarvis-text">{recordLabel(run)}</span>
              <StatusBadge status={recordStatus(run)} />
            </li>
          ))}
        </ul>
      ) : <StateMessage label="Kein Event-Stream/Run verfügbar">Der Orb bleibt im Zustand idle.</StateMessage>}
      {timeline && timeline.length > 0 && (
        <Timeline label="Mission Timeline" events={timeline.slice(0, 8).map((event, index) => ({ id: String(event.id ?? `mission-${index}`), at: String(event.createdAt ?? event.updatedAt ?? "runtime"), label: recordLabel(event), status: recordStatus(event), evidenceRef: typeof event.evidenceRef === "string" ? event.evidenceRef : null }))} />
      )}
      <div className="rounded-lg border border-jarvis-accent/20 bg-jarvis-accent/[0.04] p-3" data-orb-mission-control>
        <p className="mb-2 text-[10px] uppercase tracking-[0.14em] text-jarvis-accent">Orb-Mission-Control</p>
        <OwnerMissionControl mission={currentMission} busy={busy} error={error} onAction={(action) => void act(action)} />
        {!enabled && <p className="mt-2 text-[10px] text-jarvis-muted">Diese Studio-Ansicht bleibt read-only; die Owner-Steuerung liegt auf Home.</p>}
      </div>
      <p className="text-[10px] text-jarvis-muted">Orb-Zustand, Delegationen und Aktionen stammen ausschließlich aus AgentGraph-Runs, Common Harness, Approvals und dem value-freien Mission-Ledger.</p>
    </div>
  );
}
